import { useTranslation } from 'react-i18next';
import { Letterhead, PaperFoot } from '@/components/common';
import type { Clinic, Consent } from '@/types/api';
import { formatDate } from '../../chart/components';
import { patientFullName } from '../patientLines';

type Props = { consent: Consent; clinic: Clinic | null; logoUrl: string | null };

/**
 * Written consent to an invasive procedure (Zakon o pravima pacijenata, čl. 11
 * and 16), on the clinic's letterhead: who, what, the information given, the
 * patient's statement, and lines for the patient (or their legal
 * representative) and the dentist to sign.
 */
export function ConsentPaper({ consent: c, clinic, logoUrl }: Props) {
  const { t } = useTranslation();
  const p = c.patient;
  const doctor = c.doctor?.name ?? c.createdBy.name;
  const sections = [
    ['purpose', c.purpose],
    ['risks', c.risks],
    ['alternatives', c.alternatives],
    ['consequences', c.consequences],
  ] as const;

  return (
    <article className="offer-paper consent-paper">
      <Letterhead clinic={clinic} logoUrl={logoUrl} />

      <h1 className="offer-paper__title">{t('consents.paper.title')}</h1>
      <p className="offer-paper__fine consent-paper__law">{t('consents.paper.law')}</p>
      <dl className="offer-paper__meta">
        <dt>{t('consents.paper.number')}</dt>
        <dd>{c.number}</dd>
        <dt>{t('consents.paper.date')}</dt>
        <dd>{formatDate(c.issuedAt)}</dd>
        <dt>{t('consents.paper.patient')}</dt>
        <dd>
          <strong>{patientFullName(p)}</strong>
        </dd>
        <dt>{t('consents.paper.born')}</dt>
        <dd>{p.birthDate ? formatDate(`${p.birthDate}T12:00:00`) : '—'}</dd>
        <dt>{p.jmbg ? t('patients.form.jmbg') : t('patients.form.lbo')}</dt>
        <dd>{p.jmbg ?? p.lbo ?? '—'}</dd>
        <dt>{t('patients.form.address')}</dt>
        <dd>{p.address ?? '—'}</dd>
        {c.representativeName && (
          <>
            <dt>{t('consents.paper.representative')}</dt>
            <dd>{c.representativeName}</dd>
          </>
        )}
        <dt>{t('consents.paper.method')}</dt>
        <dd>
          <strong>{c.methodName}</strong>
          {c.site && ` · ${t('consents.paper.site', { site: c.site })}`}
        </dd>
      </dl>

      {sections.map(([key, text]) => (
        <section key={key} className="consent-paper__section">
          <h2 className="offer-paper__section">{t(`consents.paper.${key}`)}</h2>
          <p className="offer-paper__note">{text}</p>
        </section>
      ))}

      {c.note && (
        <section className="consent-paper__section">
          <h2 className="offer-paper__section">{t('consents.paper.note')}</h2>
          <p className="offer-paper__note">{c.note}</p>
        </section>
      )}

      <section className="consent-paper__statement">
        <h2 className="offer-paper__section">{t('consents.paper.statementTitle')}</h2>
        <p>
          {t(
            c.representativeName
              ? 'consents.paper.statementRepresentative'
              : 'consents.paper.statement',
            {
              method: c.methodName,
              patient: patientFullName(p),
            }
          )}
        </p>
        <p className="offer-paper__fine">{t('consents.paper.withdraw')}</p>
      </section>

      <div className="consent-paper__signs">
        <div>
          <span className="offer-paper__line" />
          <span>{c.representativeName ?? patientFullName(p)}</span>
          <span className="offer-paper__fine">
            {t(
              c.representativeName
                ? 'consents.paper.signRepresentative'
                : 'consents.paper.signPatient'
            )}
          </span>
        </div>
        <div>
          <span className="offer-paper__line" />
          <span>{doctor}</span>
          <span className="offer-paper__fine">
            {c.doctor?.licenseNumber
              ? t('consents.paper.signDoctorId', { id: c.doctor.licenseNumber })
              : t('consents.paper.signDoctor')}
          </span>
        </div>
      </div>

      <PaperFoot />
    </article>
  );
}
